import { allTrackers } from "app/services/track/track";
import type { CustomAttribute, Primitive } from "@similarweb/sw-track";

export interface IKeywordGroupDataForTracking {
    /* eslint-disable @typescript-eslint/naming-convention */
    Name?: string;
    Id: string;
    Keywords?: string[];
    /* eslint-enable @typescript-eslint/naming-convention */
}

class KeywordsGroupsTrackingEventProperties implements Record<CustomAttribute, Primitive> {
    [key: string]: Primitive;
    /* eslint-disable @typescript-eslint/naming-convention */
    "Asset Type": string = "Keyword List";
    "Asset Name": string;
    "Asset ID": string;
    "Num Keywords in List": number;
    /* eslint-enable @typescript-eslint/naming-convention */
}
class GroupTracker {
    createGroupFromSidebar() {
        return allTrackers.trackEvent("Pop Up", "open", "Create New Keyword Group");
    }

    createGroupFromAddToMenu(keyword) {
        return allTrackers.trackEvent("Drop Down", "click", `Add to/Create New Keyword Group/${keyword}`);
    }

    addToGroupFromMenu(keyword, group) {
        return allTrackers.trackEvent("Drop Down", "click", `Add to/${keyword}/${group.Name}`);
    }
    onDeleted(keywordsGroup: IKeywordGroupDataForTracking) {
        const properties: KeywordsGroupsTrackingEventProperties = this.createGroupTrackingProperties(keywordsGroup);
        allTrackers.dispatchTrackEvent("Asset Deleted", "Asset Deleted", "Asset Deleted", properties);
    }
    onCreated(keywordsGroup: IKeywordGroupDataForTracking) {
        const properties: KeywordsGroupsTrackingEventProperties = this.createGroupTrackingProperties(keywordsGroup);
        allTrackers.dispatchTrackEvent("Asset Created", "Asset Created", "Asset Created", properties);
    }

    private createGroupTrackingProperties(data: IKeywordGroupDataForTracking) {
        return {
            /* eslint-disable @typescript-eslint/naming-convention */
            "Asset Type": "Keyword List",
            "Asset Name": data?.Name ?? "UNKNOWN",
            "Asset ID": data?.Id ?? "UNKNOWN",
            "Num Keywords in List": data?.Keywords?.length ?? -1,
            /* eslint-enable @typescript-eslint/naming-convention */
        };
    }
}
export class NewGroupTracker extends GroupTracker {
    onPaste(keywords) {
        return allTrackers.trackEvent("Pop Up", "click", `Create New Keyword Group/Paste Keywords/${keywords.length}`);
    }

    onAdd({ text }) {
        return allTrackers.trackEvent("Pop Up", "click", `Create New Keyword Group/Add Keyword/${text}`);
    }

    onRemove({ text }) {
        return allTrackers.trackEvent("Pop Up", "click", `Create New Keyword Group/Remove keyword/${text}`);
    }

    onEdit({ text }) {
        return allTrackers.trackEvent("Pop Up", "click", `Create New Keyword Group/Edit keyword/${text}`);
    }

    onError(error) {
        return allTrackers.trackEvent("Pop Up", "warning", `Create New Keyword Group/Error/${error}`);
    }

    onSave(title, items) {
        return allTrackers.trackEvent("Pop Up", "click", `Create New Keyword Group/Save/${title};${items.length}`);
    }

    onView(title, items) {
        return allTrackers.trackEvent(
            "Pop Up",
            "click",
            `Keyword Group created successfully/View keyword Group/${title};${items.length}`,
        );
    }

    onClose() {
        return allTrackers.trackEvent("Pop Up", "click", `Keyword Group created successfully/Close`);
    }

    onCancel() {
        return allTrackers.trackEvent("Pop Up", "click", `Create New Keyword Group/Cancel`);
    }
}

class ExistingGroupTracker extends GroupTracker {
    onPaste(keywords) {
        return allTrackers.trackEvent("Pop Up", "click", `Edit Keyword Group/Paste Keywords/${keywords.length}`);
    }

    onAdd({ text }) {
        return allTrackers.trackEvent("Pop Up", "click", `Edit Keyword Group/Add Keyword/${text}`);
    }

    onRemove({ text }) {
        return allTrackers.trackEvent("Pop Up", "click", `Edit Keyword Group/Remove keyword/${text}`);
    }

    onEdit({ text }) {
        return allTrackers.trackEvent("Pop Up", "click", `Edit Keyword Group/Edit keyword/${text}`);
    }

    onError(error) {
        return allTrackers.trackEvent("Pop Up", "warning", `Edit Keyword Group/Error/${error}`);
    }

    onSave(title, items) {
        return allTrackers.trackEvent("Pop Up", "click", `Edit Keyword Group/Save/${title};${items.length}`);
    }

    onView() {
        return allTrackers.trackEvent("Pop Up", "click", `Keyword Group edited successfully/View keyword Group`);
    }

    onClose() {
        return allTrackers.trackEvent("Pop Up", "click", `Keyword Group edited successfully/Close`);
    }

    onCancel() {
        return allTrackers.trackEvent("Pop Up", "click", `Edit Keyword Group/Cancel`);
    }
}

export const KeywordsGroupsTracking = {
    existingGroupTracker: new ExistingGroupTracker(),
    newGroupTracker: new NewGroupTracker(),
    genericGroupTracker: new GroupTracker(),
};
